/* eslint-disable max-len */
import { Box, Breadcrumbs, Grid, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NewDialogPopup from '../../../../components/Common/NewDialogPopup/NewDialogPopup';
import { LanguageContext } from '../../../../libs/Context';
import MainCard from '../../../../UiComponents/Cards/MainCard';
import Branding from './Components/Branding';
import Engagement from './Components/Engagement';
import PollsTable from './Components/Poll';
import Registration from './Components/Registrations';
import ScheduleTimeline from './Components/ScheduleTimeline';
import CreateWebinar from './Components/CreateWebinar';
import Speakers from './Components/Speakers';
import WebinarDetailing from './Components/WebinarDetailings';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import MeetingIcon from '../../../../assets/SideNavIcons/Meeting.svg';
import SurveyDetails from './Components/NewSurvey';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { updateMeeting } from '../../../actions/meeting_actions';
import { catchErr } from '../../../../libs/HelperContent';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export const WebinarContext = createContext<any>({
	form            : {},
	setForm         : (data) => {},
	handleBack      : (data) => {},
	handleDeletePop : (data) => {},
	handleNext      : (data) => {}
});

const ScheduleLayout = (props) =>
{
	const { language } = useContext(LanguageContext);
	const [ activeStep, setActiveStep ] = useState(1);
	const maxSteps = 8;
	const navigate = useNavigate();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [ deletePop, setDeletePop ] = useState(false);
	const isMobileSM = useMediaQuery((themes: Theme) => themes.breakpoints.down('md'));
	const location = useLocation();
	const type = useParams()?.type;

	const startTime = useMemo(() =>
	{
		const now = new Date();
		const remainder = now.getMinutes() % 5;
		const offset = remainder > 0 ? 5 - remainder : 0;

		now.setMinutes(now.getMinutes() + offset, 0, 0);

		return now.valueOf();
	}, []);

	const initialState = {
		id                 : '',
		// create webinar step 1
		meetingName        : '',
		meetingDescription : '',
		startTimeUTC       : dayjs(startTime).valueOf(),
		endTimeUTC         : dayjs(startTime).add(30, 'minutes')
			.valueOf(),
		scheduleType     : 'webinar',
		duration         : 30,
		timeZone         : Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata',
		participantLimit : 2,
		meetingPassword  : '', // 18 code review changes done
		onlyInvities     : false,
		recording        : false,
		attachments      : [],
		// shareTo      : [ 'yash2@yopmail.com', 'reena@yopmail.com' ],
		// metaData     : { externalMembers: [ 'anaonymous@yopmai.com', 'aryan@yopmail.com' ] },
		shareTo          : [ ],
		metaData         : { externalMembers: [ ] },

		// registration step 2
		withRegistration   : false,
		moderation         : 1,
		bannerImage        : [ { url: `${process.env.REACT_APP_S3_CLOUDFRONT}/public/webinar/defaultbanner/banner1.jpg`, key: 'public/webinar/defaultbanner/banner1.jpg', name: 'banner1', size: 0, mediaType: 'image', type: 'image/jpg' } ],
		registrationFields : [
			{ field: 'Full Name', type: 'string', isRequired: true },
			{ field: 'Email', type: 'both', isRequired: true }
		],
		survyQues : [],
		// engagment : [],

		// branding step = 3
		webinarBackground : [],
		webinarIntroVideo : {},
		webinarLogo       : [],
		webinarTheme      : {
			primary : '#042531',
			navBar  : ' #043040'
		},
		// engagement
		chat      : false,
		polls     : false,
		reactions : false,
		resources : false,
		surveys   : false
	};
	const webinarData = location.state?.editData ?? false;
	const editData = useMemo(() =>
	{
		if (type === 'edit' && webinarData)
		{
			const newEditObj = {
				id       : webinarData?._id,
				...webinarData,
				...webinarData?.permissions,
				password : {
					isPassword      : Boolean(webinarData?.meetingPassword),
					meetingPassword : webinarData?.meetingPassword
				}
				// metaData : { externalMembers:
				// webinarData?.shareTo.filter((item) =>
				// emailPattern.test(item)) }
				// chage later for external members
			};

			delete newEditObj.meetingDetails;

			return newEditObj;
		}
		else return false;
	}, []);
	const [ form, setForm ] = useState<any>(editData || { ...initialState });
	const isCompletedRef = useRef(false);

	const handleClose = () =>
	{
		navigate('/webinars');
	};

	// to show delete popup or directly close webinar
	const handleDeletePop = (latestData?) =>
	{
		if (!latestData) // if not pressed cancel
		{
			setDeletePop(true);

			return;
		}

		const getKey = () =>
		{
			switch (activeStep)
			{
				case (1):
					return [ 'meetingName', 'timeZone', 'timezoneObj', 'startTimeUTC', 'participantLimit', 'duration', 'attachments', 'shareTo', 'metaData', 'scheduleType', 'endTimeUTC', 'onlyInvities', 'recording', 'meetingPassword' ];
				case (2):
					return [ 'bannerImage', 'withRegistration', 'moderation', 'registrationFields', 'registrationFields' ];
				default:
					return [];
			}
		};
		// if pressed cancel then checking data is changed or not
		const keyList = getKey();
		const isDataChanged = keyList.some((key) =>
			JSON.stringify(form[key]) !== JSON.stringify(latestData[key]));

		if (keyList.length === 0 || isDataChanged) setDeletePop(true);
		else handleClose();
	};

	const handleNext = () =>
	{
		setActiveStep((prevActiveStep) =>
		{
			if (prevActiveStep < maxSteps)
			{
				return prevActiveStep + 1;
			}
			else
			{
				handleClose();

				return prevActiveStep;
			}
		});
	};

	const handleBack = () =>
	{
		setActiveStep((prevActiveStep) =>
		{
			if (prevActiveStep > 1)
			{
				return prevActiveStep - 1;
			}
			else
			{
				return prevActiveStep;
			}
		});
	};

	const handleSkipFinish = () =>
	{
		setActiveStep(8);
	};

	// eslint-disable-next-line no-console
	console.log('form***', form);

	const handleDeleteWebinar = async (forceTerminate = false, shouldClose = true) =>
	{
		// to delete webinar in creation mode else discard changes in edit mode
		const body = {
			actionType : 'cancelMeeting',
			id         : form?.id
		};

		if (!form?.id || type === 'edit')
		{
			shouldClose && handleClose();

			return;
		}

		try
		{
			const res = await updateMeeting(body);

			if (res?.status)
			{
				isCompletedRef.current = true;
				toast.error(forceTerminate === true ? 'Sorry, you have lost your changes' : 'Webinar discarded', {
					position  : toast.POSITION.TOP_RIGHT,
					className : 'toast-message'
				});
				shouldClose && handleClose();
			}
		}
		catch (err)
		{
			catchErr(err);
		}
	};

	const stopExitAndReload = async (event) =>
	{
		const message = 'Are you sure you want to leave? Changes may not be saved.';

		event.returnValue = message;
		await handleDeleteWebinar(true, false);

		navigate('/webinars');

		return message;
	};

	useEffect(() =>
	{
		window.addEventListener('beforeunload', stopExitAndReload);

		return () =>
		{
			window.removeEventListener('beforeunload', stopExitAndReload);
			if (type === 'create' && isCompletedRef.current === false && form?.id)
			{
				handleDeleteWebinar(true, false);
			}
		};
	}, [ form?.id ]);

	const renderCompObj = {
		1 :	<CreateWebinar setForm={setForm} form={form} handleNextCallBack={handleNext} handleDeletePop={handleDeletePop}/>,
		2 :	<Registration form={form} setForm={setForm} handleBackCallBack={handleBack} handleSkipFinish={handleSkipFinish} handleNextCallBack={handleNext} handleDeletePop={handleDeletePop} />,
		3 :	<Branding form={form} setForm={setForm} handleBackCallBack={handleBack} handleSkipFinish={handleSkipFinish} handleNextCallBack={handleNext} handleDeletePop={handleDeletePop} />,
		4 :	<Speakers form={form} handleBackCallBack={handleBack} handleSkipFinish={handleSkipFinish} handleNextCallBack={handleNext} handleDeletePop={handleDeletePop}/>,
		5 :	<PollsTable form={form} handleBackCallBack={handleBack} handleSkipFinish={handleSkipFinish} handleNextCallBack={handleNext} handleDeletePop={handleDeletePop}/>,
		6 : <SurveyDetails form={form} activeStep={activeStep} handleDeletePop={handleDeletePop} handleBack={handleBack} handleNextCallBack={handleNext} handleSkipFinish={handleSkipFinish}/>,
		7 : <Engagement activeStep={activeStep} handleDeletePop={handleDeletePop} handleBack={handleBack} setForm={setForm} handleNext={handleNext} form={form} />
	};

	const value = useMemo(() => ({
		form,
		setForm,
		handleBack,
		handleDeletePop,
		handleNext
	}), [ form, setForm ]);

	return (
		<WebinarContext.Provider value={value}>
			<Grid container padding={1} justifyContent={'space-between'} top={isMobile ? 'var(--pixel64)' : 'var(--pixel56)'} alignItems={'center'} sx={{ backgroundColor: 'var(--whiteColor)' }} position={'sticky'} height={isMobileSM ? 'auto' : 'var(--pixel64)'} zIndex={99}>
				<Grid item>
					<Stack direction={'row'} alignItems={'center'} p='var(--pixel8)'>
						<Typography pl='var(--pixel8)'>
							<Breadcrumbs aria-label='breadcrumb' separator={<NavigateNextIcon fontSize='small' />} >
								<Stack direction='row' alignItems='center'>
									<Box
										component='img'
										src={MeetingIcon}
										alt='Meeting Room Icon'
										sx={{ filter: 'invert(49%) sepia(98%) saturate(365%) hue-rotate(145deg) brightness(93%) contrast(92%)', marginRight: 1 }}
										height={'var(--pixel18)'}
										width={'var(--pixel18)'}
									/>
									<Typography onClick={handleDeletePop} variant='inherit' color={'primary'} className='pointer'>
										{language?.webinar}</Typography>
								</Stack>

								<Typography variant='inherit' color={'textSecondary'}>{language?.scheduleWebinar}</Typography>
							</Breadcrumbs>
						</Typography>
					</Stack>
				</Grid>
			</Grid>
			<Grid container p='var(--pixel16)'>
				<Grid item xs={12}>
					<MainCard>
						<Stack
							px='var(--pixel16)'
							pb='var(--pixel16)'
							direction='row'
							display='flex'
							alignItems='center'
							justifyContent='space-between'
						>
							{activeStep === 8
								? (
									<Typography variant='h6'>Summary</Typography>
								)
								: (
									<Typography variant='h6'>
										{language?.scheduleWebinar}
									</Typography>
								)}

							<div id='webinar_preview_button' /> {/* displaying preview button here for registration screen */}

						</Stack>
						{activeStep === 8 &&
						<WebinarDetailing
							form={form}
							handleBack={handleBack}
							setActiveStep={setActiveStep}
							handleClose={handleClose}
							handleDeletePop={handleDeletePop}
							isCompletedRef={isCompletedRef}
						/>
						}
						{activeStep !== 8 &&
							<Stack direction={isMobile ? 'column' : 'row'}>
								<Grid
									item
									xs={12}
									sm={3}
									sx={{ borderRight: '1px solid rgba(0,0,0,0.12)' }}
								>
									<ScheduleTimeline
										activeStep={activeStep}
									/>
								</Grid>
								<Grid item xs={12} sm={9} pl={4}>
									{renderCompObj[activeStep] || null}
								</Grid>
							</Stack>
						}
					</MainCard>
				</Grid>
				{deletePop && (
					<NewDialogPopup
						open={deletePop}
						handleConfirm={() => setDeletePop(false)}
						closeCallBack={handleDeleteWebinar}
						type='no'
						id={undefined}
						label={language?.discradChanges}
						from='feedCreate'
						formatType='yes'
					/>
				)}
			</Grid>
		</WebinarContext.Provider>
	);
};

export default ScheduleLayout;